import { useQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain/errors';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { GET_AUDIT_ITEM_FILTER_OPTIONS } from '@internal-audit/graphql/queries/getAuditItemFilterOptions';
import { useMemo } from 'react';

/**
 * TypeScript types for the GraphQL response
 */
export interface AuditItemFilterOptions {
  statuses: string[];
  labels: string[];
  severityLevels: number[];
  multipleDecisionsOptions: string[];
  violatesPolicyOptions?: boolean[];
}

export interface GetAuditItemFilterOptionsData {
  community: {
    id: string;
    auditSession: {
      id: string;
      auditItemFilterOptions: AuditItemFilterOptions;
    };
  };
}

export interface GetAuditItemFilterOptionsVariables {
  communityId: string;
  sessionId: string;
}

/**
 * Hook to fetch audit item filter options for an audit session
 *
 * This hook fetches filter options from the server-side API endpoint,
 * ensuring that available filter options remain consistent regardless
 * of applied filters (based on complete dataset, not filtered results).
 *
 * @param communityId - The ID of the community
 * @param auditSessionId - The ID of the audit session
 * @returns Object with data, loading, error (transformed), and refetch function
 */
export function useGetAuditItemFilterOptions(communityId: string, auditSessionId: string) {
  const { data, loading, error, refetch } = useQuery<GetAuditItemFilterOptionsData, GetAuditItemFilterOptionsVariables>(GET_AUDIT_ITEM_FILTER_OPTIONS, {
    variables: { communityId, sessionId: auditSessionId },
    skip: !communityId || !auditSessionId,
    errorPolicy: 'all', // Return partial data even if there are errors
    fetchPolicy: 'cache-first', // Cache filter options since they're stable
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  // Transform errors using the standard DomainError[] pattern
  const errors = useMemo((): DomainError[] | null => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    data,
    loading,
    errors,
    refetch,
  };
}
